<template>
    <div>
        <div v-if="loaded && !cart.products.length" class="alert alert-warning">
            {{ t.checkout.empty_cart }}
        </div>
        <div v-else class="row">
            <div class="col-md-9 order-2 order-md-1">
                <div class="d-flex border-bottom mb-4">
                    <button class="col btn btn-primary" @click='changeStep(1)' >1. {{ t.checkout.personal_details }}</button>
                    <button class="col btn btn-primary" @click='changeStep(2)' :disabled="step < 2 ? true : false" >2. {{ t.checkout.shipping_information }}</button>
                    <button class="col btn btn-primary" @click='changeStep(3)' :disabled="step < 3 ? true : false" >3. {{ t.checkout.payment }}</button>
                </div>
                <transition-group name="fade">
                    <div v-if="step == 1" key="step1">
                        <h2 class="h4">1. {{ t.checkout.personal_details }}</h2>
                        <form @submit.prevent="savePersonalDetails" @keydown="formPersonalDetails.onKeydown($event)">
                            <div class="row">
                                <div class="col-md-6 mb-3">
                                    <label for="first_name">{{ t.personal_details.first_name}}<span class="small text-danger">*</span></label>
                                    <input v-model="formPersonalDetails.first_name" type="text" class="form-control" id="first_name" name="first_name">
                                    <div class="small text-danger" v-if="formPersonalDetails.errors.has('first_name')" v-html="formPersonalDetails.errors.get('first_name')" />
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label for="last_name">{{ t.personal_details.last_name}}<span class="small text-danger">*</span></label>
                                    <input v-model="formPersonalDetails.last_name" type="text" class="form-control" id="last_name" name="last_name">
                                    <div class="small text-danger" v-if="formPersonalDetails.errors.has('last_name')" v-html="formPersonalDetails.errors.get('last_name')" />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-6 mb-3">
                                    <label for="email">{{ t.personal_details.email}}<span class="small text-danger">*</span></label>
                                    <input v-model="formPersonalDetails.email" type="email" class="form-control" id="email" name="email">
                                    <div class="small text-danger" v-if="formPersonalDetails.errors.has('email')" v-html="formPersonalDetails.errors.get('email')" />
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label for="phone">{{ t.personal_details.phone}}<span class="small text-danger">*</span></label>
                                    <input v-model="formPersonalDetails.phone" type="text" class="form-control" id="phone" name="phone">
                                    <div class="small text-danger" v-if="formPersonalDetails.errors.has('phone')" v-html="formPersonalDetails.errors.get('phone')" />
                                </div>
                            </div>
                            <div v-if="!user || !user.id">
                                <div>
                                    <label class="mb-0" for="create_account"><input v-model="formPersonalDetails.create_account" type="checkbox" name="create_account" id="create_account"> {{ t.checkout.create_account}}</label>
                                </div>
                                <div class="text-secondary mb-3">({{ t.checkout.already_have_account}}, <a class="cursor-pointer text-underline" href="login?url=checkout.form">{{ t.global.log_in}}</a>)</div>
                                <transition name="fade">
                                    <div v-if="formPersonalDetails.create_account" class="mb-3">
                                        <div class="row">
                                            <div class="col-md-6 mb-3">
                                                <label for="password">{{ t.personal_details.password}}<span class="small text-danger">*</span></label>
                                                <input v-model="formPersonalDetails.password" type="password" class="form-control" id="password" name="password">
                                                <div class="small text-danger" v-if="formPersonalDetails.errors.has('password')" v-html="formPersonalDetails.errors.get('password')" />
                                            </div>
                                            <div class="col-md-6 mb-3">
                                                <label for="password_confirmation">{{ t.personal_details.password_confirmation}}<span class="small text-danger">*</span></label>
                                                <input v-model="formPersonalDetails.password_confirmation" type="password" class="form-control" id="password_confirmation" name="password_confirmation">
                                            </div>
                                        </div>
                                        <input v-model="formPersonalDetails.newsletter" type="checkbox" name="newsletter" id="newsletter" class="me-2"><label class="text-secondary" for="newsletter">{{ t.personal_details.subscribe_to_newsletter}}</label>
                                    </div>
                                </transition>
                            </div>
                            <button class="btn btn-primary" type="submit" :disabled="formPersonalDetails.busy">{{ t.checkout.next_step}}</button>
                        </form>
                    </div>
                    <div v-else-if="step == 2"  key="step2">
                        <h2 class="h4">2. {{ t.checkout.shipping_information}}</h2>
                        <form @submit.prevent="saveShippingInformation" @keydown="formShipping.onKeydown($event)">
                            <div class="h5 mb-2">{{ t.checkout.billing_information}}</div>
                            <div class="p-2 mb-2">
                                <div class="cursor-pointer" @click="showCompanyInfo ? showCompanyInfo = false : showCompanyInfo = true">{{ t.checkout.show_company_info}} ({{ t.global.optional}})<i class="ms-2 fa" :class="{'fa-chevron-down': showCompanyInfo, 'fa-chevron-right': !showCompanyInfo}"></i></div>
                                <transition name="fade">
                                    <div class="row mt-3" v-if="showCompanyInfo">
                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label for="company_name">{{ t.shipping_information.company_name}} ({{ t.global.optional}})</label>
                                                <input v-model="formShipping.company_name" type="text" class="form-control" id="company_name" name="company_name">
                                                <div class="small text-danger" v-if="formShipping.errors.has('company_name')" v-html="formShipping.errors.get('company_name')" />
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="mb-3">
                                                <label for="tax_number">{{ t.shipping_information.tax_number}} ({{ t.global.optional}})</label>
                                                <input v-model="formShipping.tax_number" type="text" class="form-control" id="tax_number" name="tax_number">
                                                <div class="small text-danger" v-if="formShipping.errors.has('tax_number')" v-html="formShipping.errors.get('tax_number')" />
                                            </div>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="address">{{ t.shipping_information.address}}<span class="small text-danger">*</span></label>
                                        <textarea v-model="formShipping.address" class="form-control" id="address" name="address" rows="5"></textarea>
                                        <div class="small text-danger" v-if="formShipping.errors.has('address')" v-html="formShipping.errors.get('address')" />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="location">{{ t.shipping_information[config.location]}}<span class="small text-danger">*</span></label>
                                        <select name="location" id="location" class="form-select" v-model="formShipping.location">
                                            <option v-for="(location, code) in location_list" :key="code" :value="code">{{ location }}</option>
                                        </select>
                                        <div class="small text-danger" v-if="formShipping.errors.has('location')" v-html="formShipping.errors.get('location')" />
                                    </div>
                                    <div class="row">
                                        <div class="mb-3 col-6">
                                            <label for="city">{{ t.shipping_information.city}}<span class="small text-danger">*</span></label>
                                            <input v-model="formShipping.city" type="text" class="form-control" id="city" name="city">
                                            <div class="small text-danger" v-if="formShipping.errors.has('city')" v-html="formShipping.errors.get('city')" />
                                        </div>
                                        <div class="mb-3 col-6">
                                            <label for="zip_code">{{ t.shipping_information.zip_code}}<span class="small text-danger">*</span></label>
                                            <input v-model="formShipping.zip_code" type="text" class="form-control" id="zip_code" name="zip_code">
                                            <div class="small text-danger" v-if="formShipping.errors.has('zip_code')" v-html="formShipping.errors.get('zip_code')" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mb-3">
                              <label for="shipping_billing"><input v-model="formShipping.shipping_billing_same" id="shipping_billing" type="checkbox"> {{ t.shipping_information.shipping_billing_addresses_same}}</label>
                            </div>
                            <div class="row" v-if="!formShipping.shipping_billing_same">
                                <div class="col-md-12 h5 mb-2">{{ t.checkout.shipping_address}}</div>
                                <div class="row">
                                    <div class="col-md-6 mb-3">
                                        <label for="shipping_first_name">{{ t.personal_details.first_name}}<span class="small text-danger">*</span></label>
                                        <input v-model="formShipping.shipping_first_name" type="text" class="form-control" id="shipping_first_name" name="shipping_first_name">
                                        <div class="small text-danger" v-if="formShipping.errors.has('shipping_first_name')" v-html="formShipping.errors.get('shipping_first_name')" />
                                    </div>
                                    <div class="col-md-6 mb-3">
                                        <label for="shipping_last_name">{{ t.personal_details.last_name}}<span class="small text-danger">*</span></label>
                                        <input v-model="formShipping.shipping_last_name" type="text" class="form-control" id="shipping_last_name" name="shipping_last_name">
                                        <div class="small text-danger" v-if="formShipping.errors.has('shipping_last_name')" v-html="formShipping.errors.get('shipping_last_name')" />
                                    </div>
                                    <div class="col-md-6 mb-3">
                                        <label for="shipping_phone">{{ t.personal_details.phone}}<span class="small text-danger">*</span></label>
                                        <input v-model="formShipping.shipping_phone" type="text" class="form-control" id="shipping_phone" name="shipping_phone">
                                        <div class="small text-danger" v-if="formShipping.errors.has('shipping_phone')" v-html="formShipping.errors.get('shipping_phone')" />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="shipping_address">{{ t.shipping_information.address}}<span class="small text-danger">*</span></label>
                                        <textarea v-model="formShipping.shipping_address" class="form-control" id="shipping_address" rows="5"></textarea>
                                        <div class="small text-danger" v-if="formShipping.errors.has('shipping_address')" v-html="formShipping.errors.get('shipping_address')" />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="location">{{ t.shipping_information[config.location]}}<span class="small text-danger">*</span></label>
                                        <select name="location" id="location" class="form-select" v-model="formShipping.shipping_location">
                                            <option v-for="(location, code) in location_list" :key="code" :value="code">{{ location }}</option>
                                        </select>
                                        <div class="small text-danger" v-if="formShipping.errors.has('shipping_location')" v-html="formShipping.errors.get('shipping_location')" />
                                    </div>
                                    <div class="row">
                                        <div class="mb-3 col-6">
                                            <label for="shipping_city">{{ t.shipping_information.city}}<span class="small text-danger">*</span></label>
                                            <input v-model="formShipping.shipping_city" type="text" class="form-control" id="shipping_city">
                                            <div class="small text-danger" v-if="formShipping.errors.has('shipping_city')" v-html="formShipping.errors.get('shipping_city')" />
                                        </div>
                                        <div class="mb-3 col-6">
                                            <label for="shipping_zip_code">{{ t.shipping_information.zip_code}}<span class="small text-danger">*</span></label>
                                            <input v-model="formShipping.shipping_zip_code" type="text" class="form-control" id="shipping_zip_code">
                                            <div class="small text-danger" v-if="formShipping.errors.has('shipping_zip_code')" v-html="formShipping.errors.get('shipping_zip_code')" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <label class="cursor-pointer" @click="showOrderNotes ? showOrderNotes = false : showOrderNotes = true">
                                {{ t.shipping_information.order_notes}} ({{ t.global.optional}})
                                <i class="ms-2 fa" :class="{'fa-chevron-down': showOrderNotes, 'fa-chevron-right': !showOrderNotes}"></i>
                            </label>
                            <transition name="fade">
                                <div v-if="showOrderNotes" class="row">
                                    <div class="col-6">
                                        <textarea v-model="formShipping.shipping_order_notes" class="form-control" id="shipping_order_notes" name="shipping_order_notes" rows="2"></textarea>
                                        <div class="small text-secondary">*{{this.__('shipping_information', 'max_characters_allowed', {'nbr': '255'}) }}</div>
                                        <div class="small text-danger" v-if="formShipping.errors.has('shipping_order_notes')" v-html="formShipping.errors.get('shipping_order_notes')" />
                                    </div>
                                </div>
                            </transition>
                            <div class="mt-2">
                                <button class="btn btn-secondary" type="button" @click="changeStep(step-1)">{{ t.checkout.back}}</button>
                                <button class="btn btn-primary" type="submit" :disabled="formShipping.busy">{{ t.checkout.next_step}}</button>
                            </div>
                        </form>
                    </div>
                    <div v-else-if="step == 3"  key="step3">
                        <h2 class="h4">3. {{ t.checkout.payment}}</h2>
                        <div v-if="config.modules.demo_mode">
                            <!-- DEMO MODE -->
                            <div v-for="payment_method in config.payment_methods" :key="payment_method" >
                                <label :for="payment_method"><input type="radio" name="payment_method" :id="payment_method"> {{t.payment[payment_method]}}</label>
                            </div>
                            <div class="mt-2">
                                <input type="checkbox" name="terms_and_conditions" id="terms_and_conditions"> <label for="terms_and_conditions">{{ t.checkout.accept_terms_and_conditions}} <a class="text-underline" :href="url('terms-and-conditions')">{{ t.checkout.terms_and_conditions}}</a><span class="small text-danger">*</span></label>
                            </div>
                            <div class="bg-light p-3 d-inline-block mt-2" v-show="formPayment.payment_method">
                                <i class="fas fa-exclamation-circle fa-lg me-1"></i> {{t.demo_mode.checkout_disclaimer}}
                            </div>
                        </div>
                        <form v-else @submit.prevent="submitPayment" @keydown="formPayment.onKeydown($event)">
                            <div class="mb-3" v-if="cart.total > 0">
                                <div class="h5 mb-3">{{ t.checkout.payment_method}}</div>
                                <div v-for="payment_method in config.payment_methods" :key='payment_method'>
                                    <label v-if="payment_method == 'credit_card_stripe'" :for="payment_method"><input @click="initStripe()" type="radio" name="payment_method" v-model="formPayment.payment_method" :id="payment_method" :value="payment_method"> {{t.payment[payment_method]}}</label>
                                    <label v-else-if="payment_method == 'paypal_button'" :for="payment_method">
                                        <input type="radio" name="payment_method" v-model="formPayment.payment_method" :id="payment_method" :value="payment_method"> {{t.payment[payment_method]}}
                                    </label>
                                    <label v-else :for="payment_method"><input type="radio" name="payment_method" v-model="formPayment.payment_method" :id="payment_method" :value="payment_method"> {{t.payment[payment_method]}}</label>
                                    <div class="mb-3" v-if="payment_method == 'atm'" v-show="formPayment.payment_method == 'atm'">
                                        <label class="w-100" for="last_digits">{{ t.checkout.last_digits}}<span class="small text-danger">*</span></label>
                                        <input type="text" class="form-control w-auto" name="last_digits" id="last_digits" v-model="formPayment.last_digits">
                                        <div class="small text-danger" v-if="formPayment.errors.has('last_digits')" v-html="formPayment.errors.get('last_digits')" />
                                    </div>
                                    <div v-if="payment_method == 'paypal_button'" v-show="formPayment.payment_method == 'paypal_button'">
                                        <div id="paypal-button-container" v-show="formPayment.payment_method == 'paypal_button'"></div>
                                        <div class="small">{{t.payment.accept_terms_and_conditions_paypal}}, <a class="text-underline" :href="url('terms-and-conditions')">{{ t.checkout.terms_and_conditions}}</a></div>
                                    </div>
                                    <div class="w-50" v-if="payment_method == 'credit_card_stripe'" v-show="formPayment.payment_method == 'credit_card_stripe'">
                                        <div id="card-element" class="form-group py-4">
                                            <!-- Elements will create input elements here -->
                                        </div>
                                        <!-- We'll put the error messages in this element -->
                                        <div v-show="cardError" class="small text-danger mt-1">{{cardError}}</div>
                                    </div>
                                </div>
                                <div class="small text-danger" v-if="formPayment.errors.has('payment_method')" v-html="formPayment.errors.get('payment_method')" />
                            </div>
                            <div v-else class="alert alert-info">{{ t.checkout.cart_is_free}}</div>
                            <div v-show="formPayment.payment_method != 'paypal_button'">
                                <input type="checkbox" name="terms_and_conditions" id="terms_and_conditions" v-model="formPayment.terms_and_conditions"> <label for="terms_and_conditions">{{ t.checkout.accept_terms_and_conditions}} <a class="text-underline" :href="url('terms-and-conditions')">{{ t.checkout.terms_and_conditions}}</a><span class="small text-danger">*</span></label>
                                <div class="small text-danger" v-if="formPayment.errors.has('terms_and_conditions')" v-html="formPayment.errors.get('terms_and_conditions')" />
                                <div v-if="formPayment.payment_method != 'credit_card_stripe'">
                                    <button class="btn btn-primary mt-3" type="submit" :disabled="formPayment.busy">{{ formPayment.busy ? t.checkout.processing+'...' : (cart.total > 0 ? t.checkout.pay : t.checkout.order) }}</button>
                                </div>
                            </div>
                        </form>
                        <button v-if="formPayment.payment_method == 'credit_card_stripe'" v-on:click="stripeProceedCheckout()" :disabled="stripePaymentProcessing" class="btn btn-primary mt-3" type="submit">{{ stripePaymentProcessing ? t.checkout.processing+'...' : t.checkout.pay}}</button>
                    </div>
                </transition-group>
            </div>
            <div class="col-md order-1 order-md-2">
                <div class="border mb-2">
                    <div class="row align-items-center mb-2" v-for="product in cart.products" :key="product.id">
                        <div class="col-3">
                            <a :href="product.url">
                                <img :src="product.thumbnail" alt="" class="img-fluid">
                            </a>
                        </div>
                        <div class="col-7 small">
                            <a :href="product.url">
                                <span class="fw-bold">{{ product.name }}</span>
                                <div v-if="product.product_variation_attributes">
                                    <div v-for="(prod_var_attr, index) in product.product_variation_attributes" :key="index"><b>{{prod_var_attr.attribute.name}}</b>: {{prod_var_attr.value.name}}</div>
                                </div>
                            </a>
                            <div v-if="product.is_gift">
                                {{ t.cart.gift_card}}: <i>"{{ product.gift_card ? product.gift_card : t.gift_card_not_specified }}"</i>
                            </div>
                            <div class="text-secondary">{{ amountWithCurrency(product.total) }}</div>
                        </div>
                        <div class="col">x{{ product.qty }}</div>
                    </div>
                </div>
                <a :href="url('cart')" class="btn btn-secondary w-100 mb-3">{{ t.checkout.edit_cart}}</a>
                <cart-summary-component></cart-summary-component>
            </div>
        </div>
    </div>
</template>

<script>
import Form from 'vform'
import store from "../CartStore";
import { loadScript } from "@paypal/paypal-js";
export default {
    store: store,
    data() {
        return {
            config: window.config,
            location_list: window.location_list,
            t: window.t,
            step: 3,
            showCompanyInfo: user && (user.customer.company_name || user.customer.tax_number) ? true : false,
            formPersonalDetails: new Form({
                first_name: user ? user.customer.first_name : '',
                last_name: user ? user.customer.last_name : '',
                email: user ? user.email : '',
                phone: user ? user.customer.phone : '',
                create_account: user ? false : true,
                password: '',
                password_confirmation: '',
                newsletter: true,
            }),
            formShipping: new Form({
                company_name: user ? user.customer.company_name : '',
                tax_number: user ? user.customer.tax_number : '',
                address: user ? user.customer.address : '',
                city: user ? user.customer.city : '',
                location: user ? user.customer.location : null,
                zip_code: user ? user.customer.zip_code : '',
                shipping_order_notes: user ? user.customer.shipping_order_notes : '',
                shipping_billing_same: user && user.customer.address != user.customer.shipping_address ? false : true,
                shipping_address: user ? user.customer.shipping_address : '',
                shipping_first_name: user ? user.customer.shipping_first_name : '',
                shipping_last_name: user ? user.customer.shipping_last_name : '',
                shipping_phone: user ? user.customer.shipping_phone : '',
                shipping_city: user ? user.customer.shipping_city : '',
                shipping_location: user ? user.customer.shipping_location : null,
                shipping_zip_code: user ? user.customer.shipping_zip_code : '',
            }),
            formPayment: new Form({
                payment_method: 'credit_card_ecpay',
                last_digits: '',
                terms_and_conditions: false
            }),
            user: user,
            stripePaymentProcessing: false,
            stripeClientSecret: null,
            paymentSuccess: false,
            stripe: null,
            card: null,
            cardError: '',
        };
    },
    watch: {
        step(newStep, oldStep) {
            if (newStep == 3) {
                this.loadPaypalScript()
            }
        }
    },
    mounted() {
        this.$store.dispatch("getCart");
        // if (this.config.payment_methods.find((method) => method == 'paypal_button')) {
        //     this.loadPaypalScript()
        // }
    },
    computed: {
        ...Vuex.mapGetters(["loaded", "cart"]),
    },
    methods: {
        loadPaypalScript () {
            var self = this
            loadScript({
                'client-id': window.paypal_client_id,
                'currency': window.config.currency_code.toUpperCase()
            })
            .then((paypal) => {
                paypal.Buttons({
                    // Sets up the transaction when a payment button is clicked
                    createOrder: (data, actions) => {
                        var value = this.config.currency_decimals ? this.cart.total/100 : this.cart.total
                        return actions.order.create({
                            noshipping: 1,
                            purchase_units: [{
                                amount: {
                                    value: value.toString() // Can also reference a variable or function
                                }
                            }]
                        });
                    },
                    // Finalize the transaction after payer approval
                    onApprove: (data, actions) => {
                        return actions.order.capture().then(function(orderData) {
                            // console.log('Capture result', orderData, JSON.stringify(orderData, null, 2));
                            axios
                            .post("ajax/checkout/paypal-payment", {
                                order_id: orderData.id
                            })
                            .catch((error) => { Vue.notify({text: error.message, type: 'error'})  })
                            .then((response)=>{
                                if (response.data.success) {
                                    window.location.href = self.url('order-confirmation/paypal_button')
                                } else {
                                    Vue.notify({text: response.data.message, type: 'error'})
                                }
                            });
                        });
                    },
                    style: {
                        layout: 'horizontal',
                        color:  'black',
                        shape:  'rect',
                        height:  34,
                        tagline: false
                    }
                })
                .render("#paypal-button-container")
                .catch((error) => {
                    console.error("failed to render the PayPal Buttons", error);
                });
            })
            .catch((error) => {
                console.error("failed to load the PayPal JS SDK script", error);
            });
        },
        async savePersonalDetails () {
            const response = await this.formPersonalDetails.post(this.url('ajax/checkout/personal-details'))
            if (response.data.success) {
                this.step = 2
                if (response.data.message) {
                    this.$notify({text: response.data.message, type: 'success'})
                }
                if (response.data.user) {
                    this.user = response.data.user
                }
            }
        },
        async saveShippingInformation () {
            const response = await this.formShipping.post(this.url('ajax/checkout/shipping-information'))
            if (response.data.success) {
                this.step = 3
                // this.formShipping = response.data.values
                this.formShipping = new Form(response.data.values)
                if (response.data.message) {
                    this.$notify({text: response.data.message, type: 'success'})
                }
                if (this.config.payment_methods.find((method) => method == 'credit_card_stripe')) {
                    this.stripeClientSecret = response.data.stripeClientSecret
                }
            }
        },
        async submitPayment () {
            const response = await this.formPayment.post(this.url('ajax/checkout/submit-payment'))
            if (response.data.success) {
                this.formPayment.busy = true
                window.location.href = response.data.url
            } else if (response.data.success == false) {
                this.$notify({text: response.data.message, type: 'error'})
                this.$store.dispatch("getCart");
            }
        },
        changeStep(newStep) {
            if(this.step > newStep) {
                this.step = newStep
            }
        },
        initStripe() {
            this.stripe = Stripe(this.config.stripe_publishable_key);
            var elements = this.stripe.elements();
            // this.card = elements.create("card", { style: {
            //     base: { color: "#f3c615" }
            // }});
            this.card = elements.create("card");
            this.card.mount("#card-element");
            this.card.on('change', ({error}) => {
                if (error) {
                    this.cardError = error.message;
                } else {
                    this.cardError = '';
                }
            });
        },
        stripeProceedCheckout() {
            var self = this
            this.stripePaymentProcessing = true

            if (!this.formPayment.terms_and_conditions) {
                this.formPayment.errors.set('terms_and_conditions', this.__('validation', 'accepted', {'attribute': this.t.checkout.terms_and_conditions}))
                this.stripePaymentProcessing = false
                return
            }

            this.stripe.confirmCardPayment(this.stripeClientSecret, {
                payment_method: {
                    card: this.card,
                    billing_details: {
                        name: this.user ? this.user.customer.first_name+' '+this.user.customer.last_name : ''
                    }
                }
            }).then(function(result) {
                if (result.error) {
                    // Show error to your customer (e.g., insufficient funds)
                    self.cardError = result.error.message;
                    self.stripePaymentProcessing = false
                } else {
                    // The payment has been processed!
                    switch (result.paymentIntent.status) {
                        //  case 'processing':
                        //     self.stripeMessage = t.stripe_processing;
                        // break;
                        case 'succeeded':
                            // Show a success message to your customer
                            // There's a risk of the customer closing the window before callback
                            // execution. Set up a webhook or plugin to listen for the
                            // payment_intent.succeeded event that handles any business critical
                            // post-payment actions.
                            window.location.href = self.url('order-confirmation/credit_card_stripe')
                        break;
                    }
                }
            });
        },
        // flushCart(){
        //     axios.post('/ajax/cart/flush');
        // },
        ...Vuex.mapActions(["applyVoucher", "deleteVoucher"]),
    },
};
</script>
